// OneDrive:IgnoreCodeCoverage

import Promise from '@ms/odsp-utilities/lib/async/Promise';
import XHRPromise from '@ms/odsp-utilities/lib/xhr/XHRPromise';
import { ITokenProvider, ISessionToken } from '../../base/tokenProvider/ITokenProvider';
import { IDataRequestor, IDataRequestorSendOptions } from './IDataRequestor';
import { Api as ApiEvent, IApiEndSchema, ResultTypeEnum } from '@ms/odsp-utilities/lib/logging/events/Api.event';
import Uri, { UriPartial } from '@ms/odsp-utilities/lib/uri/Uri';
import { IErrorResponse, GraphError, getQosExtraDataFromError, getResultCodeFromError } from '../../models/error/Error';
import { IUrlProvider } from '../dataRequestor/IDataRequestor';
import Guid from '@ms/odsp-utilities/lib/guid/Guid';

export { IDataRequestor };

/**
 * Params passed to a data requestor instance to control its overall behavior.
 */
export interface IDataRequestorParams {
    alwaysSendContentType?: boolean;
}

export interface IDataRequestorDependencies {
    sessionManagementProvider?: ITokenProvider;
    urlDataSource?: IUrlProvider;
    apiEvent?: typeof ApiEvent;
    // Origin for which cookies may be used on GET requests. This should be window.location.origin or undefined.
    cookieOrigin?: string;
    isOffline?: () => Promise<boolean>;
}

export class DataRequestor implements IDataRequestor {
    private readonly _sessionManagementProvider: ITokenProvider | undefined;
    private readonly _apiName: string | undefined;
    private readonly _apiBaseUrl: string | undefined;
    private readonly _apiEvent: typeof ApiEvent;
    private readonly _alwaysSendContentType: boolean;
    private readonly _cookieOrigin: string | undefined;
    private readonly _isOffline: (() => Promise<boolean>) | undefined;

    constructor(params: IDataRequestorParams, dependencies: IDataRequestorDependencies) {
        const {
            alwaysSendContentType = false
        } = params;

        const {
            sessionManagementProvider,
            urlDataSource,
            apiEvent = ApiEvent,
            cookieOrigin,
            isOffline
        } = dependencies;

        this._sessionManagementProvider = sessionManagementProvider;
        this._alwaysSendContentType = alwaysSendContentType;
        this._apiEvent = apiEvent;

        this._apiName = urlDataSource && typeof (urlDataSource.getApiName) === "function" ?
            urlDataSource.getApiName() :
            "Vroom";

        this._apiBaseUrl = urlDataSource ?
            (typeof (urlDataSource.getApiBaseUrl) === "function" ?
                urlDataSource.getApiBaseUrl() :
                urlDataSource.getVroomApiBaseUrl()) :
            undefined;

        this._cookieOrigin = cookieOrigin;
        this._isOffline = isOffline;
    }

    public send<T>(options: IDataRequestorSendOptions<T>): Promise<T> {
        const {
            headers = {},
            path,
            apiName,
            requestType = 'GET',
            disableLogging = false,
            timeout,
            onProgress,
            onUploadProgress,
            qosExtraData,
            apiVersion = 'v2.0',
            needsAuthorization = true,
            useAuthorizationHeaders = false,
            accessToken,
            postProcessQos,
            breakCache,
            allowCookies,
            hasContentType = true,
            useMultiPart = false
        } = options;

        const apiBaseUrl = this._apiBaseUrl;
        const cookieOrigin = this._cookieOrigin;
        // Vroom supports cookies when request is GET on same-origin.
        const useCookies = allowCookies && cookieOrigin && needsAuthorization && !accessToken && requestType === 'GET' &&
            new Uri(path[0] === '/' ? apiBaseUrl : path).getLeftPart(UriPartial.authority) === cookieOrigin;
        const sessionManagementProvider = this._sessionManagementProvider;

        const getAccessToken = needsAuthorization && !useCookies && (accessToken || sessionManagementProvider && sessionManagementProvider.getToken(apiVersion, path).catch((error: Error) => {
            // Treat auth failures as a client-side service interruption.
            return Promise.reject(new GraphError(error, {
                status: 0,
                response: {
                    error: {
                        code: 'authFailure'
                    }
                }
            }));
        })) || undefined;

        return Promise.resolve(getAccessToken).then((sessionToken: ISessionToken) => {

            let multiPartId = '';
            let requestHeaders: { [key: string]: string } = {};
            if (useMultiPart) {
                // Send only one header in the main request to avoid CORS. Remaining headers are sent as part
                // of the multi-part content disposition data.
                multiPartId = Guid.generate();
                requestHeaders = {
                    'Content-Type': `multipart/form-data;boundary=${multiPartId}`
                }
            } else {
                // Don't pass a content-type header if no postData. Otherwise we are stuck with CORS pre-flight on all requests.
                requestHeaders = {
                    'Accept': 'application/json',
                    ...(((options.postData || this._alwaysSendContentType) && hasContentType) ? {
                        'Content-Type': 'application/json'
                    } : {}),
                    ...headers
                };
            }

            let apiUri: Uri;

            if (sessionToken && sessionToken.tokenUrl && path.indexOf("/drive/") === 0) {
                // replace the generic "/drive/" with our specific token url
                apiUri = new Uri(`${sessionToken.tokenUrl}/${path.substring(7)}`);
            } else {
                apiUri = new Uri(path);
                if (apiBaseUrl && !apiUri.getScheme()) {
                    apiUri = new Uri(apiBaseUrl + path);
                }
            }

            if (useMultiPart) {
                apiUri.setQueryParameter('ump', '1');
            }

            // save the url to log before we add sensitive tokens
            let apiUrlToLog = apiUri.toString();

            if (!useMultiPart) {
                if (sessionToken) {
                    const {
                        accessToken: sessionAccessToken,
                        proofToken: proofToken,
                        wlidToken,
                        isBadger
                    } = sessionToken;

                    if (wlidToken) {
                        requestHeaders = {
                            ...requestHeaders,
                            'Authorization': `wlid1.1 ${wlidToken}`
                        };
                    } else if (useAuthorizationHeaders) {
                        if (sessionAccessToken) {
                            if (isBadger) {
                                requestHeaders = {
                                    ...requestHeaders,
                                    'Authorization': `badger ${sessionAccessToken}`
                                };
                            } else {
                                requestHeaders = {
                                    ...requestHeaders,
                                    'Authorization': `bearer ${sessionAccessToken}`
                                };
                            }
                        }

                        if (proofToken) {
                            requestHeaders = {
                                ...requestHeaders,
                                'X-PROOF_TOKEN': proofToken
                            };
                        }
                    } else {
                        if (sessionAccessToken) {
                            // We attach the access token as a query parameter to every request.
                            // We could also pass it in through the 'Authorization: bearer' header,
                            // but that would result in an extra CORS preflight request for every
                            // unique path.
                            apiUri.setQueryParameter('access_token', sessionAccessToken);
                        }

                        if (proofToken) {
                            // ODB scenarios use proof tokens
                            apiUri.setQueryParameter('prooftoken', proofToken);
                        }
                    }
                }
            }

            if (useMultiPart) {
                var body = "";
                body += `--${multiPartId}\r\n`;
                body += "Content-Disposition: form-data;name=data\r\n";
                if (headers) {
                    for (let header of Object.keys(headers)) {
                        body += `${header}: ${headers[header]}\r\n`;
                    }
                }
                body += "Content-Type: application/json\r\n";
                body += `Authorization: bearer ${sessionToken.accessToken}\r\n`;
                body += "\r\n";
                body += `${options.postData}\r\n`;
                body += `--${multiPartId}--`;
            }

            if (breakCache) {
                apiUri.setQueryParameter('r', Math.random().toFixed(8));
            }

            let apiEvent: ApiEvent;

            if (!disableLogging) {
                // wipe access_token, prooftoken, tempauth
                apiUrlToLog = apiUrlToLog.replace(/(?:(?:access_|proof)token|tempauth|authkey)=[^&]+/gi, '');
                // wipe anything JWT-shaped
                apiUrlToLog = apiUrlToLog.replace(/(?:eyJ[A-Za-z0-9_-]+(?:\.|%2E|%252E)){2}(?:[A-Za-z0-9_-]+)/gi, '$REDACTED$');

                const eventDetails = {
                    url: apiUrlToLog,
                    name: `${this._apiName}DataRequest.${apiName}`,
                    extraData: qosExtraData
                };

                apiEvent = new this._apiEvent(eventDetails);
            }

            return XHRPromise.start({
                url: apiUri.toString(),
                headers: requestHeaders,
                method: requestType,
                json: body || options.postData || '',
                requestTimeoutInMS: timeout,
                withCredentials: useCookies,
                onProgress: onProgress,
                onUploadProgress: onUploadProgress
            }).then((request: XMLHttpRequest) => {
                if (apiEvent) {
                    const qosData = {
                        resultType: ResultTypeEnum.Success
                    };
                    apiEvent.end(qosData);
                }

                if (options.parseResponse) {
                    return options.parseResponse(request);
                }

                if (request.responseText.length === 0) {
                    return null;
                }

                return <T>JSON.parse(request.responseText);
            }, (request: XMLHttpRequest | Error) => {
                const complete = (apiEndSchema: IApiEndSchema, error: Error) => {
                    if (postProcessQos) {
                        apiEndSchema = postProcessQos(apiEndSchema, error);
                    }

                    if (apiEvent) {
                        apiEvent.end(apiEndSchema);
                    }

                    return Promise.reject(error);
                };

                if (request instanceof Error) {
                    const isCanceled = Promise.isCanceled(request);
                    const resultType = isCanceled ? ResultTypeEnum.ExpectedFailure : ResultTypeEnum.Failure;

                    const apiEndSchema: IApiEndSchema = {
                        error: request.message,
                        resultCode: isCanceled ? 'Canceled' : '',
                        resultType: resultType
                    };

                    return complete(apiEndSchema, request);
                } else {
                    let response: IErrorResponse;

                    if (request.responseText) {
                        try {
                            response = <IErrorResponse>JSON.parse(request.responseText);
                        } catch (error) {
                            // no-op
                        }
                    }

                    const {
                        status,
                        statusText
                    } = request;

                    let {
                        error: {
                            message = '',
                            innerError: {
                                stackTrace = '',
                                debugMessage = ''
                            } = {}
                        } = {}
                    } = response || {};

                    const offlinePromise = (status || !this._isOffline) ? Promise.resolve(false) : this._isOffline();

                    return offlinePromise.then((isOffline: boolean) => {
                        const offlineMessage = (isOffline ? 'Offline' : '');

                        const error = new GraphError(new Error(message || offlineMessage), {
                            response: response,
                            status: status,
                            request: request
                        });

                        const resultCode = getResultCodeFromError(error);

                        const apiEndSchema: IApiEndSchema = {
                            resultType: ResultTypeEnum.Failure,
                            error: message,
                            resultCode: resultCode,
                            extraData: {
                                ...(qosExtraData || {}),
                                ...getQosExtraDataFromError(error),
                                stack: stackTrace,
                                httpStatus: status,
                                httpStatusText: statusText,
                                requestId: request.getResponseHeader("request-id") || request.getResponseHeader("sprequestguid"),
                                MSCorrelationVector: request.getResponseHeader("MS-CV"),
                                debugMessage: debugMessage,
                                accessTokenLength: (sessionToken && sessionToken.accessToken) ? sessionToken.accessToken.length : -1,
                                urlLength: apiUri.toString().length,
                                useCookies: !!useCookies,
                                // This check may give false negatives, as being on a LAN with no internet still qualifies as "onLine"
                                isOffline: isOffline
                            }
                        };

                        return complete(apiEndSchema, error);
                    });
                }
            });
        });
    }
}

export default DataRequestor;
